import React, { useState, useEffect, useMemo } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import PlusMinQty from "../../utilities/PlusMinQty";
import { X } from "lucide-react";
import { fmtINR } from "../../../util/helper";

const defSLTP = ["Price", "PNL"];

const LegRiskModal = ({ show, setShow, row, lp, info, handlessltpPrice }) => {
  const [formData, setFormData] = useState({
    sltpType: "Price",
    stopLossPrice: null,
    targetPrice: null,
    trailSLReference: null,
    trailSLStep: null,
    profitLockAt: null,
    profitLockSl: null,
    profitLockTrailRef: null,
    profitLockTrailStep: null,
    id: ""
  });
  
  const [errorMessages, setErrorMessages] = useState({ 
    stopLoss: "", 
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  
  const [slValueType, setSlValueType] = useState(0); 
  const [tpValueType, setTpValueType] = useState(0); 
  const [trailValueType, setTrailValueType] = useState(2);

  useEffect(() => {
    if(row) {
        const isPNL = row.sltpType === "PNL";
        setFormData({
            ...row,
            sltpType: row.sltpType || "Price",
            stopLossPrice: row.stopLossPrice ?? null,
            targetPrice: row.targetPrice ?? null,
            trailSLReference: row.trailSLReference ?? null,
            trailSLStep: row.trailSLStep ?? null,
            profitLockAt: row.profitLockAt ?? null,
            profitLockSl: row.profitLockSl ?? null,
            profitLockTrailRef: row.profitLockTrailReference ?? null,
            profitLockTrailStep: row.profitLockTrailStep ?? null,
            id: row.id
        });
        setSlValueType(row.stopLossValueType ?? 0);
        setTpValueType(row.targetValueType ?? 0);
        setTrailValueType(row.trailSLValueType ?? 2);
    }
  }, [show]);

  const calculateActualPrice = (value, valueType, basePrice, isStopLoss = false) => {
    if (value === null || value === undefined || value === '' || value === 0) return null;
    const val = parseFloat(value);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    const isLongPosition = row.transactionType === "BUY";
    let result;

    switch (valueType) {
      case 0: 
        result = val;
        break;
      case 1: 
        if (formData.sltpType === "Price") {
          if (isStopLoss) {
            result = isLongPosition ? base - (base * val / 100) : base + (base * val / 100);
          } else {
            result = isLongPosition ? base + (base * val / 100) : base - (base * val / 100);
          }
        } else {
          result = base + (base * val / 100);
        }
        break;
      case 2: 
        if (formData.sltpType === "Price") {
          if (isStopLoss) {
            result = isLongPosition ? base - val : base + val;
          } else {
            result = isLongPosition ? base + val : base - val;
          }
        } else if (formData.sltpType === "PNL") {
          const currentPNL = parseFloat(row.unrealizedPNL || 0);
          result = isStopLoss ? currentPNL - val : currentPNL + val;
        } else {
          result = base + val;
        }
        break;
      default:
        result = val;
    }
    
    return result;
  };

  const getBasePrice = () => {
    const basisIsPNL = formData.sltpType === "PNL";
    return basisIsPNL ? 0 : lp;
  };

  const basePrice = useMemo(() => getBasePrice(), [formData.sltpType, lp]);
  const slActualPrice = useMemo(() => 
    calculateActualPrice(formData.stopLossPrice, slValueType, basePrice, true),
    [formData.stopLossPrice, slValueType, basePrice, formData.sltpType, lp, row.transactionType]
  );
  const tpActualPrice = useMemo(() => 
    calculateActualPrice(formData.targetPrice, tpValueType, basePrice, false),
    [formData.targetPrice, tpValueType, basePrice, formData.sltpType, lp, row.transactionType]
  );
  const trailRefActualPrice = useMemo(() => {
    if (formData.trailSLReference === null || formData.trailSLReference === undefined || formData.trailSLReference === '') {
      return null;
    }
    const val = parseFloat(formData.trailSLReference);
    if (isNaN(val)) return null;
    
    if (trailValueType === 1) {
      return basePrice * val / 100; 
    } else {
      return val;
    }
  }, [formData.trailSLReference, trailValueType, basePrice]);

  const trailStepActualPrice = useMemo(() => {
    if (formData.trailSLStep === null || formData.trailSLStep === undefined || formData.trailSLStep === '') {
      return null;
    }
    const val = parseFloat(formData.trailSLStep);
    if (isNaN(val)) return null;
    
    if (trailValueType === 1) {
      return basePrice * val / 100; 
    } else {
      return val;
    }
  }, [formData.trailSLStep, trailValueType, basePrice]);

  const handleReset = () => {
    const resetFormData = {
        ...formData,
        stopLossPrice: null,
        targetPrice: null,
        trailSLReference: null,
        trailSLStep: null,
        profitLockAt: null,
        profitLockSl: null,
        profitLockTrailRef: null,
        profitLockTrailStep: null,
    };
    setFormData(resetFormData);
    setErrorMessages({
      stopLoss: "",
      target: "",
      trailSL: "",
      profitLock: "",
      profitLockTrail: ""
    });
    setSlValueType(0);
    setTpValueType(0);
    setTrailValueType(2);
  };

  const validateTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;
    
    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }
    
    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }
    
    setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;
    
    if (atVal !== null && slVal === null) {
      setErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }
    
    setErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;
    
    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }
    
    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }
    
    setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  const handleFieldChange = (val, name) => {
    if(name === "stopLossPrice") {
      isValidsltp("sl", val);
    }
    if(name === "targetPrice") {
      isValidsltp("tp", val);
    }

    let value = {
      ...formData,
      [name]: !val ? val : parseFloat(val),
    };

    if (name === "stopLossPrice" && (!val || val === 0 || val === null)) {
      value.trailSLReference = null;
      value.trailSLStep = null;
      setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    }
    
    if ((name === "profitLockAt" || name === "profitLockSl") && (!val || val === 0 || val === null)) {
      value.profitLockTrailRef = null;
      value.profitLockTrailStep = null;
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    }

    if (name === "trailSLReference" || name === "trailSLStep") {
      const newRef = name === "trailSLReference" ? value.trailSLReference : formData.trailSLReference;
      const newStep = name === "trailSLStep" ? value.trailSLStep : formData.trailSLStep;
      validateTrailSL(newRef, newStep);
    }

    if (name === "profitLockAt" || name === "profitLockSl") {
      const newAt = name === "profitLockAt" ? value.profitLockAt : formData.profitLockAt;
      const newSl = name === "profitLockSl" ? value.profitLockSl : formData.profitLockSl;
      validateProfitLock(newAt, newSl);
    }

    if (name === "profitLockTrailRef" || name === "profitLockTrailStep") {
      const newRef = name === "profitLockTrailRef" ? value.profitLockTrailRef : formData.profitLockTrailRef;
      const newStep = name === "profitLockTrailStep" ? value.profitLockTrailStep : formData.profitLockTrailStep;
      validateProfitLockTrail(newRef, newStep);
    }

    setFormData(value);
  };

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");

  const onClickSlType = () => {
    const basis = formData?.sltpType;
    const newSlValueType = basis === "PNL" ? 
      cycleWithin(slValueType, [0, 2]) : 
      cycleWithin(slValueType, [0, 1, 2]);
    
    setSlValueType(newSlValueType);
    isValidsltp("sl", formData?.stopLossPrice, newSlValueType);
  };

  const onClickTpType = () => {
    const basis = formData?.sltpType;
    const newTpValueType = basis === "PNL" ? 
      cycleWithin(tpValueType, [0, 2]) : 
      cycleWithin(tpValueType, [0, 1, 2]);
    
    setTpValueType(newTpValueType);
    isValidsltp("tp", formData?.targetPrice, newTpValueType);
  };

  const onClickTrailType = () => {
    const basis = formData?.sltpType;
    if(basis === "PNL") { 
      setTrailValueType(2); 
      return; 
    }
    setTrailValueType(prev => cycleWithin(prev, [2,1]));
  };

  const isValidsltp = (type, val, overrideType = null) => {
    const valueType = overrideType ?? (type === "sl" ? slValueType : tpValueType);
    const setMsg = (ok, customMsg = null) => {
      if (type === "sl") {
        const msg = customMsg || (formData.sltpType === "PNL" ? "Stop Loss should be below current PNL" : (row.transactionType === "BUY" ? "Stop Loss should be below current price" : "Stop Loss should be above current price"));
        setErrorMessages((prev) => ({ ...prev, stopLoss: ok ? "" : msg }));
      } else {
        const msg = customMsg || (formData.sltpType === "PNL" ? "Target should be above current PNL" : (row.transactionType === "BUY" ? "Target should be above current price" : "Target should be below current price"));
        setErrorMessages((prev) => ({ ...prev, target: ok ? "" : msg }));
      }
    };

    if (val === null || val === undefined || val === '' || val === 0) {
      setMsg(true);
      return true;
    }

    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setMsg(true);
      return true;
    }
    
    const actualPrice = type === "sl" 
      ? calculateActualPrice(parsedVal, valueType, getBasePrice(), true)
      : calculateActualPrice(parsedVal, valueType, getBasePrice(), false);

    if (actualPrice === null) {
      setMsg(true);
      return true;
    }

    if (formData.sltpType === "Price") {
      if (actualPrice <= 0) {
        setMsg(false, type === "sl" ? "Stop Loss must be positive" : "Target must be positive");
        return false;
      }
    }

    if (formData.sltpType === "PNL") {
      if (valueType === 2 && val < 0) {
        setMsg(false, type === "sl" ? "Stop Loss cannot be negative" : "Target cannot be negative");
        return false;
      }
    }

    if (formData.sltpType === "PNL") {
      const pnl = parseFloat(row.unrealizedPNL || 0);
      if (Number.isNaN(pnl)) {
        setMsg(true);
        return true;
      }

      if (type === "sl") {
        const ok = actualPrice < pnl;
        setMsg(ok);
        return ok;
      } else {
        const ok = actualPrice > pnl;
        setMsg(ok);
        return ok;
      }
    } else {
      const ref = lp;
      if (row.transactionType === "BUY") {
        if (type === "sl") {
          const ok = actualPrice < ref;
          setMsg(ok);
          return ok;
        } else {
          const ok = actualPrice > ref;
          setMsg(ok);
          return ok;
        }
      } else {
        if (type === "sl") {
          const ok = actualPrice > ref;
          setMsg(ok);
          return ok;
        } else {
          const ok = actualPrice < ref;
          setMsg(ok);
          return ok;
        }
      }
    }
  };

  const clickActionSave = () => {
    if(!isValidsltp("sl", parseFloat(formData.stopLossPrice))) {
      return;
    }
    if(!isValidsltp("tp", parseFloat(formData.targetPrice))) {
      return;
    }
    if (!validateTrailSL(formData.trailSLReference, formData.trailSLStep)) {
      return;
    }
    if (!validateProfitLock(formData.profitLockAt, formData.profitLockSl)) {
      return;
    }
    if (!validateProfitLockTrail(formData.profitLockTrailRef, formData.profitLockTrailStep)) {
      return;
    }

    const payload = {
        ...formData,
        stopLossValueType: slValueType,
        targetValueType: tpValueType,
        trailSLValueType: trailValueType,
        profitLockTrailValueType: 2,
        profitLockTrailReference: formData.profitLockTrailRef,
        profitLockTrailStep: formData.profitLockTrailStep
    };

    handlessltpPrice(payload);
    setShow(false);
  };

  const clearfiled = (name) => {
    let updatedFormData = {
     ...formData,
      [name]: null
    };

    if (name === "stopLossPrice") {
      updatedFormData.trailSLReference = null;
      updatedFormData.trailSLStep = null;
      setErrorMessages((prev) => ({ ...prev, stopLoss: "", trailSL: "" }));
    } else if (name === "profitLockAt" || name === "profitLockSl") {
      updatedFormData.profitLockTrailRef = null;
      updatedFormData.profitLockTrailStep = null;
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    } else if (name === "targetPrice") {
      setErrorMessages((prev) => ({ ...prev, target: "" }));
    }
    setFormData(updatedFormData);
  };

  const handleSltpTypeChange = (e) => {
    const val = e.target.value;
    if(formData.sltpType === val) {
        setFormData({...formData, sltpType:val})
    } else {
        setFormData({
            ...formData,
            "sltpType":val,
            "stopLossPrice": null,
            "targetPrice": null,
            "trailSLReference": null,
            "trailSLStep": null,
            "profitLockAt":null,
            "profitLockSl":null,
            "profitLockTrailRef":null,
            "profitLockTrailStep":null
        })
        setSlValueType(0);
        setTpValueType(0);
        setTrailValueType(2);
        setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="sm"
      centered
      className="fixed-modal-sltp"
    >
      <Modal.Header style={{ position: 'relative' }}>
        <Modal.Title className="modal-title-size">Leg Risk Settings</Modal.Title>
        <button type="button" className="modal-close-button" onClick={() => setShow(false)}>×</button>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-sltp">
          <div style={{justifyContent:"space-between"}} className="sltp-div modal-input ">
            <Form.Group className="form-group">
              <label className="form-label">SL/TP Basis</label>
              <Form.Select value={formData.sltpType} onChange={handleSltpTypeChange}>
                {defSLTP.map((data, index) => <option value={data} key={index}>{data}</option>)}
              </Form.Select>
            </Form.Group>
            <div className="pnl-com-p">
              {formData.sltpType === "PNL" ? 
                <p className="m-0">PNL: <span className="text-success">{row.unrealizedPNL?.toFixed(2) || "0.00"}</span></p> :
                <p className="m-0">LTP: <span>{lp.toFixed(2)}</span></p>
              }
            </div>
          </div>
          <div className="sltp-div">
            <div className="sltp-col-left">
              <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                Stop Loss
                {slActualPrice !== null && (slValueType === 1 || slValueType === 2) && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                    ({fmtINR(slActualPrice.toFixed(2))})
                  </span>
                )}
              </Form.Label>
              <div className="sltp-input-row">
                <div className={errorMessages.stopLoss ? "error-border" :""}>
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "stopLossPrice")}
                    filterValue={formData.stopLossPrice}
                    name={"stopLossPrice"}
                    formatINR={true}
                  />
                </div>
                <div className="pts-class" onClick={onClickSlType}>{formData.sltpType === "PNL" ? renderPNLSLTPLabel(slValueType) : renderTypeLabel(slValueType)}</div>
                <X size={16} onClick={()=>clearfiled("stopLossPrice")} className="sltp-delete-icon sltp-delete-icon-enabled" />
              </div>
              {errorMessages.stopLoss && <div className="text-danger mt-1" style={{ fontSize: '10px' }}>{errorMessages.stopLoss}</div>}
            </div>
            <div className="sltp-col-right">
              <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                Target
                {tpActualPrice !== null && (tpValueType === 1 || tpValueType === 2) && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                    (₹{tpActualPrice.toFixed(2)})
                  </span>
                )}
              </Form.Label>
              <div className="sltp-input-row">
                <div className={errorMessages.target ? "error-border" :""}>
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "targetPrice")}
                    filterValue={formData.targetPrice}
                    name={"targetPrice"}
                    formatINR={true}
                  />
                </div>
                <div className="pts-class" onClick={onClickTpType}>{formData.sltpType === "PNL" ? renderPNLSLTPLabel(tpValueType) : renderTypeLabel(tpValueType)}</div>
                <X size={16} onClick={()=>clearfiled("targetPrice")} className="sltp-delete-icon sltp-delete-icon-enabled" />
              </div>
              {errorMessages.target && <div className="text-danger mt-1" style={{ fontSize: '10px' }}>{errorMessages.target}</div>}
            </div>
          </div>
          <div className="sltp-div-profile">
            <p className="sltp-trail-header">Stoploss Trailing</p> 
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above">
                  For every X move
                  {trailRefActualPrice !== null && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                      ({fmtINR(trailRefActualPrice.toFixed(2))})
                    </span>
                  )}
                </Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "trailSLReference")}
                    filterValue={formData.trailSLReference}
                    name={"trailSLReference"}
                    min={0.05} step={0.05}
                    disabled={!formData.stopLossPrice}
                    formatINR={true}
                  />
                  {formData.sltpType === "PNL" ? <div className="pts-class">{renderPNLLabel()}</div> : <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>}
                  <X size={16} onClick={()=>clearfiled("trailSLReference")} className={`sltp-delete-icon ${!formData.stopLossPrice ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} />
                </div>
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above">
                  Trail SL by Y
                  {trailStepActualPrice !== null && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                      ({fmtINR(trailStepActualPrice.toFixed(2))})
                    </span>
                  )}
                </Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "trailSLStep")}
                    filterValue={formData.trailSLStep}
                    name={"trailSLStep"}
                    min={0.05} step={0.05}
                    disabled={!formData.stopLossPrice}
                    formatINR={true}
                  />
                  {formData.sltpType === "PNL" ? <div className="pts-class">{renderPNLLabel()}</div> : <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>}
                  <X size={16} onClick={()=>clearfiled("trailSLStep")} className={`sltp-delete-icon ${!formData.stopLossPrice ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} />
                </div>
              </div>
            </div>
            {errorMessages.trailSL && <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>{errorMessages.trailSL}</div>}

            <p className="sltp-trail-header">Profit Locking</p>
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above">When profit reaches</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "profitLockAt")}
                    filterValue={formData.profitLockAt}
                    name={"profitLockAt"}
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X size={16} onClick={()=>clearfiled("profitLockAt")} className="sltp-delete-icon sltp-delete-icon-enabled" />
                </div>
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above">Lock min profit at</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "profitLockSl")}
                    filterValue={formData.profitLockSl}
                    name={"profitLockSl"}
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X size={16} onClick={()=>clearfiled("profitLockSl")} className="sltp-delete-icon sltp-delete-icon-enabled" />
                </div>
              </div>
            </div>
            {errorMessages.profitLock && <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>{errorMessages.profitLock}</div>}

            <p className="sltp-trail-header">Profit Trailing</p>
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above">If profit increases by X</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailRef")}
                    filterValue={formData.profitLockTrailRef}
                    name={"profitLockTrailRef"}
                    min={0.05} step={0.05}
                    disabled={!formData.profitLockAt || !formData.profitLockSl}
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X size={16} onClick={()=>clearfiled("profitLockTrailRef")} className={`sltp-delete-icon ${(!formData.profitLockAt || !formData.profitLockSl) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} />
                </div>
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above">Trail Profit by Y</Form.Label>
                <div className="sltp-input-row">
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailStep")}
                    filterValue={formData.profitLockTrailStep}
                    name={"profitLockTrailStep"}
                    min={0.05} step={0.05}
                    disabled={!formData.profitLockAt || !formData.profitLockSl}
                    formatINR={true}
                  />
                  <div className="pts-class">{renderPNLLabel()}</div>
                  <X size={16} onClick={()=>clearfiled("profitLockTrailStep")} className={`sltp-delete-icon ${(!formData.profitLockAt || !formData.profitLockSl) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} />
                </div>
              </div>
            </div>
            {errorMessages.profitLockTrail && <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>{errorMessages.profitLockTrail}</div>}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleReset} variant="outline-primary">Reset</Button>
        <Button disabled={!!errorMessages.stopLoss || !!errorMessages.target || !!errorMessages.trailSL || !!errorMessages.profitLock || !!errorMessages.profitLockTrail} onClick={clickActionSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LegRiskModal;
